/**
 * What "high quality" means, as numbers a renderer can act on.
 *
 * The presets exist so that quality is a decision made once, at the session
 * level, rather than a set of magic numbers scattered through renderers. A
 * renderer is handed a `QualityContext` and decides for itself what to do with
 * it — `RIPPLE` reads `capacity` and `segmentScale`, a text layer reads
 * `textureScale`, and a future smoke shader will read `iterationScale` — but
 * none of them decides *how high* quality should be.
 *
 * The multipliers are deliberately modest at HIGH and aggressive at ULTRA.
 * ULTRA is not meant to run at frame rate; offline rendering is where it earns
 * its keep, because there the cost of a frame is patience rather than jitter.
 */

export const QUALITY_PRESETS = ['LIVE', 'HIGH', 'ULTRA'] as const;
export type QualityPreset = (typeof QUALITY_PRESETS)[number];

export function isQualityPreset(value: string): value is QualityPreset {
  return (QUALITY_PRESETS as readonly string[]).includes(value);
}

/** What a renderer is told about the quality it should produce. */
export interface QualityContext {
  readonly preset: QualityPreset;
  /** Multiplier on pooled object counts — ripples, particles. */
  readonly capacityScale: number;
  /** Multiplier on geometric detail — segments, subdivisions. */
  readonly segmentScale: number;
  /** Multiplier on generated texture sizes. */
  readonly textureScale: number;
  /** Multiplier on iterative work — blur passes, march steps. */
  readonly iterationScale: number;
  /** Device pixel ratio the frame is rendered at. */
  readonly pixelRatio: number;
  /** Whether the post chain should use its expensive path. */
  readonly highQualityPost: boolean;
}

const PRESETS: Readonly<Record<QualityPreset, Omit<QualityContext, 'preset'>>> = {
  LIVE: {
    capacityScale: 1,
    segmentScale: 1,
    textureScale: 1,
    iterationScale: 1,
    pixelRatio: 1,
    highQualityPost: false,
  },
  HIGH: {
    capacityScale: 2,
    segmentScale: 2,
    textureScale: 1.5,
    iterationScale: 1.5,
    pixelRatio: 1,
    highQualityPost: true,
  },
  ULTRA: {
    capacityScale: 4,
    segmentScale: 3,
    textureScale: 2,
    iterationScale: 2.5,
    pixelRatio: 1,
    highQualityPost: true,
  },
};

export function qualityContext(preset: QualityPreset): QualityContext {
  return { preset, ...PRESETS[preset] };
}

// ---------------------------------------------------------------------------

export const ASPECT_RATIOS = ['16:9', '9:16', '1:1'] as const;
export type AspectRatio = (typeof ASPECT_RATIOS)[number];

export function isAspectRatio(value: string): value is AspectRatio {
  return (ASPECT_RATIOS as readonly string[]).includes(value);
}

export function aspectValue(aspect: AspectRatio): number {
  switch (aspect) {
    case '9:16':
      return 9 / 16;
    case '1:1':
      return 1;
    default:
      return 16 / 9;
  }
}

/** Named heights. Width follows from the aspect ratio, not the other way round. */
export const RESOLUTIONS = ['720p', '1080p', '1440p', '4K'] as const;
export type ResolutionName = (typeof RESOLUTIONS)[number];

export function isResolutionName(value: string): value is ResolutionName {
  return (RESOLUTIONS as readonly string[]).includes(value);
}

const LONG_EDGE: Readonly<Record<ResolutionName, number>> = {
  '720p': 1280,
  '1080p': 1920,
  '1440p': 2560,
  '4K': 3840,
};

export interface FrameSize {
  readonly width: number;
  readonly height: number;
}

/**
 * Turns a name and an aspect into pixels.
 *
 * The named resolution is the **long edge**, so `1080p` at `9:16` is 1080×1920
 * rather than 1920×1080 rotated into a letterbox. That is what a vertical
 * export is expected to mean, and it is why the aspect ratio has to be known
 * before the size can be computed.
 *
 * Both dimensions are rounded to even numbers because every video encoder this
 * will eventually feed requires it, and discovering that in Phase 7 with a
 * folder of odd-sized PNGs would be a poor time to learn it.
 */
export function frameSize(resolution: ResolutionName, aspect: AspectRatio): FrameSize {
  const long = LONG_EDGE[resolution];
  const ratio = aspectValue(aspect);
  const width = ratio >= 1 ? long : Math.round(long * ratio);
  const height = ratio >= 1 ? Math.round(long / ratio) : long;
  return { width: even(width), height: even(height) };
}

/**
 * Clamps a requested size to what the device will actually draw.
 *
 * A WebGL context has a hard `MAX_TEXTURE_SIZE`, and asking for a render target
 * beyond it does not fail loudly — it produces an incomplete framebuffer and a
 * black frame, which looks like a bug in the artwork rather than a limit of the
 * machine. Downgrading to the next size that fits, and saying so, is better
 * than either crashing or silently rendering nothing.
 */
export function fitToDevice(
  size: FrameSize,
  maxDimension: number,
): { readonly size: FrameSize; readonly downgraded: boolean; readonly reason: string | null } {
  const longest = Math.max(size.width, size.height);
  if (maxDimension <= 0 || longest <= maxDimension) {
    return { size, downgraded: false, reason: null };
  }
  const scale = maxDimension / longest;
  const fitted: FrameSize = {
    width: even(Math.max(2, Math.floor(size.width * scale))),
    height: even(Math.max(2, Math.floor(size.height * scale))),
  };
  return {
    size: fitted,
    downgraded: true,
    reason:
      `${size.width}x${size.height} exceeds this device's ${maxDimension}px limit; ` +
      `rendering at ${fitted.width}x${fitted.height}`,
  };
}

function even(value: number): number {
  const rounded = Math.round(value);
  return rounded % 2 === 0 ? rounded : rounded + 1;
}
